import React, { useEffect, useState } from 'react';

import { useGetFace } from '@vlabs/api/luna-api/client';
import { useGetCard } from '@vlabs/api/point-api/client';
import { ButtonSwitch, ButtonSwitchGroup } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import { GoToCardButton } from '../../features/go-to-card';
import { AttachFaceToCardButton } from '../attach-face-to-card-button';
import {
  useVmsFromSourceName,
  VideoArchiveWidget,
} from '../video-archive';
import { EventTopMatchInfo } from './components/EventTopMatchInfo';
import st from './EventTopMatchPanel.module.sass';

const TABS = {
  video: 'video',
  info: 'info',
};

function EventTopMatchPanel({
  event,
  canViewVideoArchive = true,
  canReadCard = true,
  canViewCardDetails = true,
  canCreateCard = true,
}) {
  const { t } = useTranslation();
  const topMatch = event?.top_match;
  const matchType = topMatch?.face_id ? 'face' : 'event';
  const getFace = useGetFace(topMatch?.face_id ?? '', undefined, {
    query: {
      enabled: matchType === 'face' && !!topMatch?.face_id,
    },
  });
  const cardId = getFace.data?.external_id;
  const getCard = useGetCard(cardId, undefined, {
    query: {
      enabled: !!cardId,
    },
  });
  const { vmsSource } = useVmsFromSourceName(event?.source);
  const [activeTab, setActiveTab] = useState(TABS.video);

  useEffect(() => {
    if (vmsSource && canViewVideoArchive) {
      setActiveTab(TABS.video);
    } else if (topMatch) {
      setActiveTab(TABS.info);
    }
  }, [topMatch, vmsSource, canViewVideoArchive]);

  if (!topMatch && !vmsSource) {
    return (
      <div className={st.NoMatchDummy}>
        {t('lunapoint:панель оператора.no data')}
      </div>
    );
  }

  return (
    <div className={st.Root}>
      <div className={st.ButtonsSection}>
        {canViewVideoArchive && (
          <ButtonSwitchGroup
            className={st.SwitchGroup}
            data-testid="topMatchSwitch"
            onChange={setActiveTab}
            size="small"
            value={activeTab}
            variant="square"
          >
            <ButtonSwitch
              disabled={!vmsSource}
              value={TABS.video}
            >
              {t('lunapoint:map stream.toggle buttons.video')}
            </ButtonSwitch>
            <ButtonSwitch
              disabled={!topMatch}
              value={TABS.info}
            >
              {t('lunapoint:карточка персоны.информация')}
            </ButtonSwitch>
          </ButtonSwitchGroup>
        )}

        {canReadCard && canViewCardDetails && !!getCard.data?.card_id && (
          <GoToCardButton cardId={getCard.data.card_id} />
        )}

        {canReadCard
          && canCreateCard
          && activeTab === TABS.info
          && matchType === 'face'
          && !!getFace.data?.face_id
          && !getCard.data && (
          <AttachFaceToCardButton
            data-testid="FaceId.addToFaceCard"
            faceId={getFace.data.face_id}
            onAttachFaceToCard={() => getCard.refetch()}
            size="small"
          />
        )}
      </div>

      <div className={st.MainSection}>
        {activeTab === TABS.info && !!topMatch && (
          <EventTopMatchInfo
            cardId={canReadCard ? cardId : undefined}
            key={topMatch.face_id ?? topMatch.event_id}
            topMatch={topMatch}
          />
        )}

        {canViewVideoArchive && activeTab === TABS.video && !!vmsSource && (
          <div className={st.VideoArchive}>
            <VideoArchiveWidget
              startTime={event?.create_time}
              vmsSource={vmsSource}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export { EventTopMatchPanel };
